import { createStyles, IconButton, makeStyles, Typography } from "@material-ui/core";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import React from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router";
import { getPublishedExams } from "../../api/ExamApi";
import { ExamTemplateIndex, ExamTypeTranslationKey } from "../../model/Exam";
import { GetTranslatedValue } from "../../model/Shared/TranslatedValue";
import { routesPaths } from "../../Routes";
import { GetTranslation } from "../../translations/GetTranslation";
import { ColumnDefinition, CustomGrid } from "../common/customGrid/CustomGrid";
import TranslatedMessage from "../translations/TranslatedMessage";
import ExamSelectionDialog from "./ExamSelectionDialog";

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      marginTop: "3rem",
    },
    header: {
      marginBottom: "-2rem",
    },
  })
);

type ModuleFormExamsProps = {
  exams: ExamTemplateIndex[];
  handleAddExams: (exams: ExamTemplateIndex[]) => void;
  handleRemoveExam: (exam: ExamTemplateIndex) => void;
  isReadOnly: boolean;
};

const ModuleFormExams = (props: ModuleFormExamsProps) => {
  const { exams, handleAddExams: addExamItems, handleRemoveExam, isReadOnly } = props;
  const [allExams, setAllExams] = React.useState<ExamTemplateIndex[]>([]);

  const history = useHistory();
  const intl = useIntl();

  React.useEffect(() => {
    getPublishedExams().then((exams) => {
      setAllExams(exams);
    });
  }, []);

  const [selectionDialogOpened, setSelectionDialogOpened] = React.useState(false);
  const classes = useStyles();

  const columnsDefinitions: ColumnDefinition<ExamTemplateIndex>[] = [
    {
      columnKey: "examName",
      columnLabel: <TranslatedMessage id="exam.field.examName" />,
      cellValue: (_) => GetTranslatedValue(_.name),
    },
    { columnKey: "examId", columnLabel: <TranslatedMessage id="exam.field.examId" />, cellValue: (_) => _.examId },
    {
      columnKey: "examType",
      columnLabel: <TranslatedMessage id="exam.field.type" />,
      cellValue: (_) => GetTranslation(intl, ExamTypeTranslationKey[_.type]),
    },
  ];

  const onNewExamSelection = (exams: ExamTemplateIndex[]) => {
    setSelectionDialogOpened(false);
    addExamItems(exams);
  };

  const getSelectableExams = () => {
    let selectableExams = allExams.filter((x) => exams.every((y) => y.id !== x.id));
    return selectableExams;
  };

  if (!isReadOnly) {
    columnsDefinitions.push({
      columnKey: "removeModule",
      columnLabel: "",
      cellValue: (_) => (
        <IconButton
          color="secondary"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            handleRemoveExam(_);
          }}
        >
          <HighlightOffIcon />
        </IconButton>
      ),
      isSortingDisabled: true,
    });
  }

  return (
    <React.Fragment>
      <Typography variant="h6" className={isReadOnly ? "" : classes.header}>
        <TranslatedMessage id="exam.header" />
      </Typography>
      <CustomGrid<ExamTemplateIndex>
        columnDefinitions={columnsDefinitions}
        rows={exams}
        rowKey={(_) => _.id}
        addButtonTooltip={<TranslatedMessage id="exam.button.new" />}
        onRowClick={(row: ExamTemplateIndex) => history.push(routesPaths.exam(row.id))}
        onAddClick={() => setSelectionDialogOpened(true)}
        hideSearchBar={true}
        hideAddButton={isReadOnly}
      />

      <ExamSelectionDialog
        handleAccept={onNewExamSelection}
        handleCancel={() => setSelectionDialogOpened(false)}
        isOpened={selectionDialogOpened}
        selectableExams={getSelectableExams()}
      />
    </React.Fragment>
  );
};

export default ModuleFormExams;
